import {
  VISUAL_COMPONENT_BACKGROUNDS,
  VISUAL_COMPONENT_BORDERS,
  VISUAL_COMPONENT_HIGHLIGHTED_BACKGROUNDS,
  VISUAL_COMPONENT_TEXT,
} from "@/domains/colors/colorsets"
import { ColorSet } from "@/domains/colors/types"
import { BodyCaption } from "@/domains/design/components/body-caption"
import { BodyXs } from "@/domains/design/components/body-xs"
import { MoveRightIcon } from "@/domains/icons"
import { cn, formatCurrency, formatPercentage } from "@/domains/library"
import type { LockDisplayData } from "@/domains/lock-page/components/card/0.story-mocks"

const getColorFromSet = (diff: number, isScalest: boolean, colorSet: ColorSet) => {
  if (diff === 0) return colorSet.default
  return isScalest ? colorSet.scalest : colorSet.scalar
}

export const LockIncreaseVisualizer = ({
  currentLock,
  futureLock,
}: {
  currentLock: LockDisplayData | undefined
  futureLock: LockDisplayData | undefined
}) => {
  if (!currentLock || !futureLock) return null

  const futureTotalAmount = futureLock.lockAmount
  const amountIncrease = futureTotalAmount - currentLock.lockAmount
  const hasIncrease = amountIncrease > 0
  let percentageIncrease = 0
  let currentP = 100
  let futureP = 0

  if (hasIncrease) {
    percentageIncrease = amountIncrease / currentLock.lockAmount // display value
    const relativePercentage = amountIncrease / futureTotalAmount // real value
    currentP = 100 - relativePercentage * 100
    futureP = relativePercentage * 100
  }

  const currentBg = getColorFromSet(percentageIncrease, futureLock.isScalest, VISUAL_COMPONENT_BACKGROUNDS)
  const futureBg = getColorFromSet(percentageIncrease, futureLock.isScalest, VISUAL_COMPONENT_HIGHLIGHTED_BACKGROUNDS)
  const border = getColorFromSet(percentageIncrease, futureLock.isScalest, VISUAL_COMPONENT_BORDERS)
  const innerText = getColorFromSet(percentageIncrease, futureLock.isScalest, VISUAL_COMPONENT_TEXT)

  return (
    <div>
      <div className="mb-1 flex flex-row items-center justify-between px-1">
        <BodyCaption As="div" className="text-t300">
          {formatCurrency(currentLock.lockAmount)} Locked
        </BodyCaption>
        <BodyCaption As="div" className="text-t300">
          {percentageIncrease > 0 ? (
            <div className="flex flex-row items-center gap-1">
              <MoveRightIcon size={14} />
              {formatCurrency(futureLock.lockAmount)}
            </div>
          ) : (
            ""
          )}
        </BodyCaption>
      </div>
      <div
        className={cn(
          "relative flex h-[32px] w-full flex-row items-center justify-between",
          "overflow-hidden rounded-xl",
          "border border-p100-light",
          "bg-p300",
          border,
        )}
      >
        <div
          style={{ width: `${currentP}%` }}
          className={cn("absolute left-0 top-0 z-0 h-[32px] py-2 text-center", currentBg)}
        ></div>
        <div
          style={{ width: `${futureP}%` }}
          className={cn(
            "absolute right-0 top-0 z-0 h-[32px] py-2 text-center",
            hasIncrease && "border-l-2 border-l-white/20",
            futureBg,
          )}
        ></div>

        <BodyXs As="div" className={cn("z-10 flex w-full items-center justify-center", innerText)}>
          {formatPercentage(percentageIncrease)} Increase
        </BodyXs>
      </div>
      <div className="mt-1 flex flex-row items-center justify-between px-1">
        <BodyCaption As="div" className="text-t300">
          {formatCurrency(currentLock.lockValue)} veSCALE
        </BodyCaption>

        <BodyCaption As="div" className="text-t300">
          {percentageIncrease > 0 ? (
            <div className="flex flex-row items-center gap-1">
              <MoveRightIcon size={14} />
              {formatCurrency(futureLock.lockValue)}
            </div>
          ) : (
            ""
          )}
        </BodyCaption>
      </div>
    </div>
  )
}

